(() => {
  const namespace = "GuruCatalogControls";
  if (window[namespace]) return;

  const translationCache = new WeakMap();
  const normalizeText = (value) => `${value || ""}`.trim().replace(/\s+/g, " ");
  const isNonNegativeSafeInteger = (value) => Number.isSafeInteger(value) && value >= 0;
  const isPositiveSafeInteger = (value) => isNonNegativeSafeInteger(value) && value > 0;

  const create = ({ rootSelector, surface, i18nSelector, fallbackTranslations = {} }) => {
    if (!rootSelector || !surface || !i18nSelector) throw new Error("Catalog controls configuration is incomplete");

    const rootFor = (node) => node?.closest?.(rootSelector) || document.querySelector(rootSelector);
    const sectionFor = (root) => root?.closest?.(".shopify-section");
    const dialogFor = (root) => root?.querySelector(`[data-guru-${surface}-filter-dialog]`);
    const formFor = (root) => root?.querySelector(`[data-guru-${surface}-filter-form]`);

    const translationsFor = (root) => {
      if (!root) return fallbackTranslations;
      const cached = translationCache.get(root);
      if (cached) return cached;
      let parsed = {};
      try {
        const nodes = root.querySelectorAll(`:scope > script${i18nSelector}`);
        if (nodes.length !== 1) throw new Error("Catalog locale data must be unique");
        parsed = JSON.parse(nodes[0].textContent || "{}");
      } catch (_error) {
        parsed = {};
      }
      const translations = Object.fromEntries(Object.entries(fallbackTranslations).map(([key, fallback]) => (
        [key, typeof parsed[key] === "string" && parsed[key].trim() ? parsed[key] : fallback]
      )));
      translationCache.set(root, translations);
      return translations;
    };

    const localizedCopy = (root, key, replacements = {}) => {
      let value = translationsFor(root)[key] || fallbackTranslations[key] || "";
      Object.entries(replacements).forEach(([token, replacement]) => {
        value = value.replaceAll(`__${token.toUpperCase()}__`, `${replacement}`);
      });
      return value;
    };

    const productNoun = (root, count) => localizedCopy(root, Number(count) === 1 ? "product" : "products");
    const setBusy = (root, busy) => root?.setAttribute("aria-busy", String(busy));

    const syncQuickFilters = (root) => {
      if (!root) return;
      ["availability", "sale"].forEach((type) => {
        const inputs = Array.from(root.querySelectorAll(`[data-guru-${type}-input]`));
        const pressed = inputs.some((input) => input.checked);
        const disabled = inputs.length === 0 || inputs.every((input) => input.disabled && !input.checked);
        root.querySelectorAll(`[data-guru-${surface}-quick-filter="${type}"]`).forEach((button) => {
          button.disabled = disabled;
          button.setAttribute("aria-pressed", String(pressed));
        });
        root.querySelectorAll(`[data-guru-panel-quick-control="${type}"]`).forEach((control) => {
          control.checked = pressed;
          control.disabled = disabled;
        });
      });
    };

    const parseSection = (html, sectionId) => {
      const parsed = new DOMParser().parseFromString(html, "text/html");
      const matches = Array.from(parsed.querySelectorAll("[id]"))
        .filter((element) => element.id === `shopify-section-${sectionId}`);
      return matches.length === 1 ? matches[0] : null;
    };

    const matchingFacetInput = (form, field) => Array.from(form?.elements || []).find((candidate) => (
      candidate instanceof HTMLInputElement &&
      candidate.type === field.type &&
      candidate.name === field.name &&
      candidate.value === field.value
    ));

    const resetFacetEvidence = (form) => {
      if (!form) return;
      form.querySelectorAll("[data-guru-initial-disabled]").forEach((control) => {
        control.disabled = control.dataset.guruInitialDisabled === "true";
      });
      form.querySelectorAll("[data-guru-facet-count][data-guru-initial-count]").forEach((count) => {
        count.textContent = count.dataset.guruInitialCount;
      });
    };

    return Object.freeze({
      dialogFor,
      formFor,
      isNonNegativeSafeInteger,
      isPositiveSafeInteger,
      localizedCopy,
      matchingFacetInput,
      normalizeText,
      parseSection,
      productNoun,
      resetFacetEvidence,
      rootFor,
      sectionFor,
      setBusy,
      syncQuickFilters,
      translationsFor,
    });
  };

  window[namespace] = Object.freeze({ create });
})();
